import { useSyncExternalStore } from "react";
import { createGarage, createRestorePoints, createRpc, createSync, deviceId, indexedDb, noLocalDb, pendingCount, syncText } from "@hachi/api";
import { LEGACY_KEYS, newId, toISODate } from "@hachi/core";
import type { GarageState } from "@hachi/core";
import { auth, backendReady, store, supabaseConfig } from "../account/backend";

/** The one garage on this device, and its sync with the account. Plain module, no React, so sign-in can use it too. */

function openDb() {
  try {
    return globalThis.indexedDB ? indexedDb() : noLocalDb;
  } catch {
    return noLocalDb;
  }
}

export const garage = createGarage({ kv: store, db: openDb() });
export const restorePoints = createRestorePoints(store, () => newId("rp"));

/** The first time the app opens each day, a copy is kept (`dailyPoint`). */
export function dailyPoint() {
  const st = garage.state(), day = toISODate(new Date());
  if (st.meta.lastAutoPoint === day) return;
  restorePoints.add(st, "Start of the day");
  garage.replace({ ...st, meta: { ...st.meta, lastAutoPoint: day } });
}

const rpc = createRpc({ auth, ...supabaseConfig });
let onRejected: () => void = () => undefined;
/** The account layer re-checks when the server rejects this device. */
export const whenRejected = (fn: () => void) => { onRejected = fn; };

export const sync = createSync({
  rpc: (fn, args) => rpc(fn, args),
  deviceId: () => deviceId(store),
  userId: () => auth.session()?.uid ?? null,
  getState: garage.state,
  setState: (st: GarageState) => garage.replace(st),
  loadSync: garage.loadSync,
  saveSync: garage.saveSync,
  onStatus: () => { statusVersion++; for (const l of statusListeners) l(); },
  onRejected: () => onRejected(),
  onUserChanged: () => store.removeItem(LEGACY_KEYS.restorePoints),
});

let statusVersion = 0;
const statusListeners = new Set<() => void>();

const DEBOUNCE_MS = 1500;
const LOOP_MS = 60_000;
const RETRY_MS = [5000, 15_000, 30_000];

let timer: ReturnType<typeof setTimeout> | null = null;
let failures = 0;
let loop: ReturnType<typeof setInterval> | null = null;
let stopListening: (() => void) | null = null;

export function syncSoon(ms = DEBOUNCE_MS) {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { void runSync(); }, ms);
}

async function runSync() {
  if (!navigator.onLine) return;
  await sync.now();
  // Retries with growing gaps; the minute loop carries on after that.
  if (sync.status().lastErr === "network" || sync.status().lastErr === "server") {
    const wait = RETRY_MS[failures++];
    if (wait) syncSoon(wait);
  } else {
    failures = 0;
  }
}

/** While the app is open: sync after edits, every minute, and when coming back online or to the app. */
export function startSync() {
  if (!backendReady || loop) return;
  loop = setInterval(() => { if (document.visibilityState === "visible") void runSync(); }, LOOP_MS);
  const unsubscribe = garage.subscribe(() => { if (pendingCount(garage.state(), garage.loadSync())) syncSoon(); });
  const onOnline = () => { statusVersion++; for (const l of statusListeners) l(); syncSoon(200); };
  const onOffline = () => { statusVersion++; for (const l of statusListeners) l(); };
  const onVisible = () => { if (document.visibilityState === "visible") syncSoon(200); };
  addEventListener("online", onOnline);
  addEventListener("offline", onOffline);
  document.addEventListener("visibilitychange", onVisible);
  stopListening = () => {
    unsubscribe();
    removeEventListener("online", onOnline);
    removeEventListener("offline", onOffline);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

export function stopSync() {
  if (loop) clearInterval(loop);
  loop = null;
  if (timer) clearTimeout(timer);
  timer = null;
  stopListening?.();
  stopListening = null;
}

/** Ask the browser not to clear this site's data under storage pressure. */
export function keepDataSafe() {
  try {
    if (navigator.storage?.persist && !/firefox/i.test(navigator.userAgent)) void navigator.storage.persist().catch(() => undefined);
  } catch {
    // Not supported: the data is still saved, just with less protection.
  }
}

export const storageOk = () => garage.storageOk();

export function useGarage(): GarageState {
  useSyncExternalStore(garage.subscribe, garage.version);
  return garage.state();
}

const subscribeAll = (fn: () => void) => {
  statusListeners.add(fn);
  const off = garage.subscribe(fn);
  return () => { statusListeners.delete(fn); off(); };
};
const snapshot = () => `${statusVersion}.${garage.version()}`;

/** Live sync status for the footer, Settings and the warnings. */
export function useSyncStatus() {
  useSyncExternalStore(subscribeAll, snapshot);
  const pending = pendingCount(garage.state(), garage.loadSync());
  const status = sync.status();
  const online = navigator.onLine;
  return { pending, online, failed: !!status.lastErr, refused: status.refused, ...syncText(pending, online, status) };
}
